import { Check, ChevronDown, Clock } from "lucide-react";
import { FadingRuler } from "../shared/FadingRuler";
import {
  TIMELINE_MACRO_LEVELS,
  TIMELINE_RANGES,
  TIMELINE_THREADS,
} from "./timeline-model";

interface TimelineFiltersProps {
  dropdownOpen: boolean;
  macroFilter: Set<number>;
  selectedThread: string;
  threadCounts: Map<string, number>;
  timeRange: string;
  onDropdownToggle: () => void;
  onMacroToggle: (level: number) => void;
  onThreadSelect: (slug: string) => void;
  onTimeRangeChange: (range: string) => void;
}

export function TimelineFilters(props: TimelineFiltersProps) {
  const activeThread =
    TIMELINE_THREADS.find((thread) => thread.slug === props.selectedThread) ??
    TIMELINE_THREADS[0];

  return (
    <div className="relative z-10 shrink-0">
      <FadingRuler className="opacity-45" />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2">
        <div
          className="flex items-center gap-0.5"
          aria-label="Timeline severity"
        >
          {TIMELINE_MACRO_LEVELS.map(({ level, label, color }) => {
            const active = props.macroFilter.has(level);
            return (
              <button
                key={level}
                type="button"
                aria-pressed={active}
                onClick={() => props.onMacroToggle(level)}
                className={`flex items-center gap-1 rounded px-1.5 py-1 text-[9px] uppercase tracking-[0.1em] transition-colors ${
                  active
                    ? "bg-[var(--fintheon-accent)]/8 text-[var(--fintheon-text)]/82"
                    : "text-[var(--fintheon-muted)]/42 hover:text-[var(--fintheon-text)]/68"
                }`}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: color }}
                />
                {label}
              </button>
            );
          })}
        </div>
        <FadingRuler orientation="vertical" className="h-4 opacity-45" />
        <div className="flex items-center gap-0.5" aria-label="Timeline range">
          <Clock className="mr-0.5 h-3 w-3 text-[var(--fintheon-muted)]/40" />
          {TIMELINE_RANGES.map((range) => (
            <button
              key={range.key}
              type="button"
              aria-pressed={props.timeRange === range.key}
              onClick={() => props.onTimeRangeChange(range.key)}
              className={`rounded px-1.5 py-1 font-mono text-[9px] transition-colors ${
                props.timeRange === range.key
                  ? "bg-[var(--fintheon-accent)]/8 text-[var(--fintheon-accent)]"
                  : "text-[var(--fintheon-muted)]/42 hover:text-[var(--fintheon-text)]/68"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>
      <FadingRuler className="opacity-35" />
      <div className="relative px-4 py-2">
        <button
          type="button"
          aria-expanded={props.dropdownOpen}
          onClick={props.onDropdownToggle}
          className="flex w-full items-center justify-between rounded px-1 py-1.5 text-left text-[12px] text-[var(--fintheon-text)] transition-colors hover:text-[var(--fintheon-accent)]"
        >
          <span className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-sm"
              style={{ backgroundColor: activeThread.color }}
            />
            {activeThread.title}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${props.dropdownOpen ? "rotate-180" : ""}`}
          />
        </button>
        {props.dropdownOpen ? (
          <div className="fintheon-popover-surface absolute left-4 right-4 top-full z-50 max-h-[300px] overflow-y-auto py-1">
            {TIMELINE_THREADS.map((thread) => {
              const selected = props.selectedThread === thread.slug;
              return (
                <button
                  key={thread.slug}
                  type="button"
                  onClick={() => props.onThreadSelect(thread.slug)}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] transition-colors ${
                    selected
                      ? "bg-[var(--fintheon-accent)]/8 text-[var(--fintheon-text)]"
                      : "text-[var(--fintheon-muted)]/62 hover:text-[var(--fintheon-text)]"
                  }`}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-sm"
                    style={{ backgroundColor: thread.color }}
                  />
                  <span>{thread.title}</span>
                  {thread.slug === "all" ? null : (
                    <span className="ml-auto font-mono text-[9px] text-[var(--fintheon-muted)]/38">
                      {props.threadCounts.get(thread.slug) ?? 0}
                    </span>
                  )}
                  {selected ? (
                    <Check className="h-3 w-3 text-[var(--fintheon-accent)]" />
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <FadingRuler className="opacity-35" />
    </div>
  );
}
